import { useUiStore } from '@stores/ui';
import {
  BroadcastChannelEnum,
  BroadcastChannelMessageEnum,
} from '@shared/enum/broadcast-channel.enum';
import {
  IMessageEventLiveness,
  IMessageEventVideo,
} from '@shared/interface/service-worker.interface';
import { IVideo } from '@shared/interface/model.interface';
import { logger } from './logger/logger-manager';
import {
  dbGetCachedVideos,
  dbRemoveCachedVideos,
  dbSetVideoSize,
} from '@data/datasource/local/cached-video.database';
import { flatten } from './tools.lib';
import { dbGetPrograms } from '@data/datasource/local/program.database';
import { ServiceWorkerEventEnum } from '@shared/enum/service-worker.enum';

/**
 * Browser request to service worker : remove a video from the cache storage
 * @param videoIdList
 */
export const removeVideoFromCache = (videoIdList: Array<number>) => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.controller?.postMessage({
      type: BroadcastChannelMessageEnum.DELETE_CACHED_VIDEOS,
      videoIdList,
    });
  }
};

/**
 * Browser request to service worker : remove a video from the cache storage
 * @param videoIdList
 */
export const removeAllVideosFromCache = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.controller?.postMessage({
      type: BroadcastChannelMessageEnum.DELETE_ALL_CACHED_VIDEOS,
    });
  }
};

/**
 * Service Worker listeners
 */
export const serviceWorkerListeners = () => {
  // Video listener
  swVideoListener();

  // Liveness listener
  // swLivenessListener();
};

/**
 * Service Worker listener : Video
 */
export const swVideoListener = () => {
  const broadcastResponse = new BroadcastChannel(
    BroadcastChannelEnum.SW_CHANNEL_VIDEO_CACHE_RESPONSE,
  );

  broadcastResponse.onmessage = (event: MessageEvent<IMessageEventVideo>) => {
    if (event.data) {
      // Remove all videos to the cache storage
      if (
        event.data.serviceWorkerEvent != null &&
        event.data.serviceWorkerEvent ===
          ServiceWorkerEventEnum.ALL_CACHED_VIDEO_DELETED
      ) {
        logger?.info('[Service Worker] - Listener - deleted all cached video');

        removeAllCachedVideos();
      }

      // Remove a video to the cache storage
      if (
        event.data.serviceWorkerEvent != null &&
        event.data.serviceWorkerEvent ===
          ServiceWorkerEventEnum.CACHED_VIDEO_DELETED
      ) {
        logger?.info('[Service Worker] - Listener - deleted cached video');

        updateCachedVideoList();
      }

      // Add a video to the cache storage
      if (
        event.data.serviceWorkerEvent != null &&
        event.data.serviceWorkerEvent ===
          ServiceWorkerEventEnum.CACHED_VIDEO_ADDED
      ) {
        logger?.info(
          `[Service Worker] - Listener - get freshly cached video (id: ${event.data.cachedVideoId}, at : ${event.data.cachedVideoAt})`,
        );

        updateCachedVideoList();
      }
    }
  };
};

/**
 * Update uiStore with cached video list
 */
export const updateCachedVideoList = async () => {
  // Init uiStore
  const uiStore = useUiStore();

  // Init data (program && cachedVideoList)
  const programs = uiStore.programs ?? [];
  const cachedVideoList = await dbGetCachedVideos();

  for (let video of cachedVideoList) {
    const videoFound = flatten<IVideo>(programs.map((p) => p.videos)).find(
      (v) => v.id === video.id,
    );
    if (videoFound) {
      video = Object.assign(videoFound, { ...video });
    }
  }

  uiStore.cachedVideoList = cachedVideoList;
  uiStore.totalAmountOfVideoCached = cachedVideoList.reduce(
    (prev, curr) => prev + (curr.fileSize ?? 0),
    0,
  );
};

export const removeAllCachedVideos = () => {
  // Init uiStore
  const uiStore = useUiStore();

  // Remove in memory
  uiStore.cachedVideoList = [];
  uiStore.totalAmountOfVideoCached = 0;

  // Remove in local database
  dbRemoveCachedVideos();
};

/**
 * Function triggered when login to clean and update video size
 */
export const checkAndUpdateVideoSize = async () => {
  const cachedVideos = await dbGetCachedVideos();
  const programs = await dbGetPrograms();

  for (const video of cachedVideos) {
    const videos = flatten<IVideo>(
      programs.filter(
        (p) =>
          p.videos != null &&
          p.videos.length > 0 &&
          p.videos.some((v) => v.id === video.id),
      ),
    );

    const videoFound = Array.isArray(videos)
      ? videos.find((v) => v.id === video.id)
      : videos;

    if (videoFound != null && video.fileSize !== videoFound.fileSize) {
      await dbSetVideoSize({
        videoId: video.id ?? 0,
        size:
          (video.fileSize ?? 0) > (videoFound.fileSize ?? 0)
            ? video.fileSize ?? 0
            : videoFound.fileSize ?? 0,
      });
    }
  }
};
